import { useEffect } from 'react';
import { usePhonePreferences } from '@/features/settings/usePhonePreferences';

/**
 * IU-4 (`docs/concept-iphone-ux.md#iu-4-phone-type-and-spacing`, *Settings*): the stored
 * `User.preferences.phone.density` reaching the one attribute `phone-type.css` keys its Compact
 * rule on, `data-phone-density`. Without this, Density changes nothing outside the Settings
 * screen itself — a setting that changes nothing must not ship (team-lead, 2026-10-04).
 *
 * Mounted once, in `Layout.tsx`'s top level, under the store provider `usePhonePreferences` reads
 * (`useStores().authStore`) — the reason this couldn't live beside `usePhoneTypeScaleEffect.ts` in
 * `index.tsx`, outside that provider, until now.
 */
export function usePhoneDensityAttribute(): void {
  const { preferences } = usePhonePreferences();
  const density = preferences?.density ?? 'comfortable';

  useEffect(() => {
    if (density === 'compact') {
      document.documentElement.setAttribute('data-phone-density', density);
    } else {
      document.documentElement.removeAttribute('data-phone-density');
    }
    return () => {
      document.documentElement.removeAttribute('data-phone-density');
    };
  }, [density]);
}

/** A render-nothing mount point for the effect above — see its own doc comment for why here. */
export function PhoneDensityAttribute(): null {
  usePhoneDensityAttribute();
  return null;
}
